import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  AVISO_DESCARGA_SIN_SELECCION,
  excedeLimiteDescarga,
  MAX_REGISTROS_DESCARGA,
} from "../../../utils/limiteDescarga";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Title, Tooltip, Legend);

/**
 * Pestaña "Experimento A/B" de Postulaciones (solo perfil adm_ti). Todas las cifras salen de
 * GET experiment/metrics/{code} de cyber-service: una persona = una asignación del sorteo.
 * Las asignaciones forzadas con ?ab= (pruebas) no cuentan salvo que se active el filtro.
 */

// ─── constants ────────────────────────────────────────────────────────────────

const VARIANT_COLORS = ["#2563eb", "#7c3aed", "#0d9488", "#d97706"];
const GOOD = "#15803d";
const BAD = "#b91c1c";
const MUTED = "#64748b";
const INK = "#0f172a";

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  ACTIVO: { label: "Activo", color: "#166534", bg: "#dcfce7" },
  PAUSADO: { label: "Pausado", color: "#92400e", bg: "#fef3c7" },
  BORRADOR: { label: "Borrador", color: "#475569", bg: "#e2e8f0" },
  FINALIZADO: { label: "Finalizado", color: "#b91c1c", bg: "#fee2e2" },
};

const READING_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  CONTROL: { label: "Control", color: "#1d4ed8", bg: "#dbeafe" },
  MEJOR: { label: "Mejor", color: "#166534", bg: "#dcfce7" },
  PEOR: { label: "Peor", color: "#b91c1c", bg: "#fee2e2" },
  SIN_DIFERENCIA: { label: "Sin diferencia", color: "#475569", bg: "#e2e8f0" },
  MUESTRA_INSUFICIENTE: { label: "Muestra insuficiente", color: "#92400e", bg: "#fef3c7" },
};

const PRIMARY_LABEL: Record<string, string> = {
  M1_POSTULO: "M1 · Postuló",
  M2_COMPLETA_EN_VENTANA: "M2 · Completa en ventana",
};

const FUNNEL_LABEL: Record<string, string> = {
  ASIGNADAS: "Asignadas",
  VIERON_FORMULARIO: "Vieron el formulario",
  POSTULARON: "Postularon",
  COMPLETA_EN_ETAPA_1: "Completas en el primer paso",
  WHATSAPP_ENVIADO: "WhatsApp enviado",
  ABRIO_ENLACE: "Abrieron el enlace",
  COMPLETA: "Completas",
  COMPLETA_EN_VENTANA: "Completas en ventana",
};

const SEGMENT_LABEL: Record<string, string> = {
  ETAPA_1: "Llenar el paso 1",
  ESPERA_WHATSAPP: "Espera hasta el WhatsApp",
  REACCION: "Reacción (WhatsApp → abre)",
  ETAPA_2: "Llenar el paso 2",
  LLENADO: "Llenando, sin espera (paso 1 + paso 2)",
  TOTAL: "Total, con espera",
};

// Campo del formulario (data-form-field / id en teamwork-form) → nombre legible.
const FIELD_LABEL: Record<string, string> = {
  rut: "RUT",
  nombres: "Nombres",
  apellidos: "Apellidos",
  fechaNacimiento: "Fecha de nacimiento",
  genero: "Género",
  estadoCivil: "Estado civil",
  nacionalidad: "Nacionalidad",
  tipoVisa: "Tipo de visa",
  disponibilidad: "Disponibilidad",
  turno: "Turno",
  cargo: "Cargo",
  correo: "Correo",
  telefono: "Teléfono",
  whatsapp: "WhatsApp",
  direccion: "Dirección",
  comuna: "Comuna",
  hijos: "Número de hijos",
  salud: "Sistema de salud",
  afp: "AFP",
  banco: "Banco",
  tipoCuenta: "Tipo de cuenta",
  numeroCuenta: "Número de cuenta",
  tallaPolera: "Talla de polera",
  tallaPantalon: "Talla de pantalón",
  calzado: "Calzado",
  nombreEmergencia: "Contacto de emergencia",
  telefonoEmergencia: "Teléfono de emergencia",
  parentesco: "Parentesco",
  tieneEnfermedad: "¿Tiene enfermedad?",
  descripcionEnfermedad: "Descripción de la enfermedad",
  tieneDiscapacidad: "¿Tiene discapacidad?",
  descripcionDiscapacidad: "Descripción de la discapacidad",
  cedulaFrontal: "Cédula (frente)",
  cedulaTrasera: "Cédula (reverso)",
  carnetFrontal: "Licencia (frente)",
  carnetTrasera: "Licencia (reverso)",
  curriculum: "Currículum",
};

type FieldSort = "mediana" | "aporte" | "orden";

const FIELD_SORTS: Array<{ key: FieldSort; label: string }> = [
  { key: "mediana", label: "Más lentos de llenar" },
  { key: "aporte", label: "Más peso en el tiempo total" },
  { key: "orden", label: "Orden del formulario" },
];

const EXPOSURE_LABEL: Record<string, string> = {
  SIN_IDENTIFICAR: "Visitó sin identificarse",
  ASIGNADO: "Asignada al experimento",
  NO_ELEGIBLE: "No elegible",
  FUERA_DE_TRAFICO: "Fuera del tráfico",
  SIN_EXPERIMENTO: "Sin experimento",
  ERROR: "Error de sorteo",
};

const HEALTH_LABEL: Record<string, string> = {
  SORTEOS_CON_ERROR_24H: "Sorteos con error (24 h)",
  ENVIOS_PROGRAMADOS_ATRASADOS_15MIN: "Envíos programados atrasados (> 15 min)",
  ENVIOS_CON_ERROR_24H: "Envíos de WhatsApp con error (24 h)",
  PARCIALES_SIN_INVITACION_30MIN: "Parciales sin invitación (> 30 min)",
  INVITACIONES_VENCIDAS_SIN_MARCAR: "Invitaciones vencidas sin marcar",
  SINCRONIZACION_LEGACY_CON_ERROR: "Sincronización legacy con error",
};

const DEVICE_LABEL: Record<string, string> = {
  movil: "Móvil",
  tablet: "Tablet",
  escritorio: "Escritorio",
  desconocido: "Desconocido",
};

type MetricKind = "count" | "pct" | "min";

interface MetricRow {
  key: string;
  label: string;
  help: string;
  kind: MetricKind;
  // Solo aplica a variantes por etapas (en COMPLETO se muestra "No aplica").
  stagesOnly?: boolean;
  // Hacia dónde es mejor la diferencia contra el control (colorea el Δ).
  better?: "up" | "down";
}

interface MetricSection {
  title: string;
  description: string;
  rows: MetricRow[];
}

const METRIC_SECTIONS: MetricSection[] = [
  {
    title: "Conversión",
    description: "¿Cuántos postulan y cuántos se van sin intentarlo?",
    rows: [
      { key: "asignadas", label: "Personas asignadas", help: "Personas nuevas que el sorteo asignó a la variante.", kind: "count" },
      { key: "distribucion_pct", label: "Distribución", help: "Porcentaje del total de asignadas que cayó en la variante.", kind: "pct" },
      { key: "vieron_formulario", label: "Vieron el formulario", help: "Asignadas a las que se les mostró el formulario.", kind: "count" },
      { key: "postularon", label: "Postularon", help: "Personas con postulación creada (en ETAPAS basta con el primer paso).", kind: "count" },
      { key: "conversion_pct", label: "Tasa de conversión", help: "Postularon / asignadas.", kind: "pct", better: "up" },
      { key: "conversion_vio_pct", label: "Conversión sobre quienes vieron", help: "Postularon / vieron el formulario (base de la métrica M1).", kind: "pct", better: "up" },
      { key: "rebote_pct", label: "Tasa de rebote", help: "Vieron el formulario y se fueron sin intentar enviarlo / vieron el formulario.", kind: "pct", better: "down" },
      { key: "abandono_pct", label: "Tasa de abandono", help: "Asignadas que no postularon / asignadas.", kind: "pct", better: "down" },
      { key: "error_envio_pct", label: "Con error al enviar", help: "Personas con algún error de envío / intentaron enviar.", kind: "pct", better: "down" },
      { key: "abrieron_opcionales", label: "Abrieron los campos opcionales", help: "Expandieron el desplegable de campos opcionales en el primer paso.", kind: "count", stagesOnly: true },
    ],
  },
  {
    title: "Etapas y completitud",
    description: "¿Cuántos terminan la postulación completa?",
    rows: [
      { key: "completaron_en_primer_paso", label: "Completaron todo en el primer paso", help: "COMPLETO: todas las postulaciones. ETAPAS: quienes llenaron también los opcionales y no necesitaron el segundo formulario.", kind: "count" },
      { key: "solo_primer_paso", label: "Llenaron solo el primer paso", help: "Postularon en ETAPAS enviando solo la etapa 1.", kind: "count", stagesOnly: true },
      { key: "en_etapa_1", label: "Hoy en etapa 1", help: "Postulación parcial que todavía no abre el enlace del segundo formulario.", kind: "count", stagesOnly: true },
      { key: "en_etapa_2", label: "Hoy en etapa 2", help: "Abrieron el segundo formulario pero no lo han enviado.", kind: "count", stagesOnly: true },
      { key: "completaron_etapa_2", label: "Completaron el 2º formulario", help: "Enviaron la etapa 2.", kind: "count", stagesOnly: true },
      { key: "conversion_entre_etapas_pct", label: "Conversión entre etapas", help: "Completaron el 2º formulario / llenaron solo el primer paso.", kind: "pct", stagesOnly: true, better: "up" },
      { key: "completas", label: "Postulaciones completas", help: "Personas con todas las etapas enviadas.", kind: "count" },
      { key: "completas_pct", label: "Completas sobre asignadas", help: "Completas / asignadas.", kind: "pct", better: "up" },
      { key: "completas_sobre_postulaciones_pct", label: "Completas sobre postulaciones", help: "Completas / postularon.", kind: "pct", better: "up" },
      { key: "completas_en_ventana_pct", label: "Completas dentro de la ventana", help: "Maduras completas dentro de la ventana / maduras.", kind: "pct", better: "up" },
    ],
  },
  {
    title: "WhatsApp y segundo formulario",
    description: "¿Llega el mensaje, lo abren y terminan?",
    rows: [
      { key: "mensaje_pendiente", label: "Pendientes por recibir el mensaje", help: "Invitación programada que aún no se envía (retraso sorteado).", kind: "count", stagesOnly: true },
      { key: "mensaje_enviado", label: "Mensaje enviado", help: "El WhatsApp salió hacia el proveedor.", kind: "count", stagesOnly: true },
      { key: "mensaje_entregado", label: "Mensaje recibido (entregado)", help: "El proveedor confirmó la entrega en el teléfono.", kind: "count", stagesOnly: true },
      { key: "entrega_pct", label: "Tasa de entrega", help: "Entregados / enviados.", kind: "pct", stagesOnly: true, better: "up" },
      { key: "mensaje_leido", label: "Mensaje leído", help: "El proveedor confirmó la lectura.", kind: "count", stagesOnly: true },
      { key: "mensaje_error", label: "Envío con error", help: "Invitaciones en estado ERROR.", kind: "count", stagesOnly: true },
      { key: "abrieron_segundo_formulario", label: "Abrieron el 2º formulario", help: "Abrieron el enlace del WhatsApp al menos una vez.", kind: "count", stagesOnly: true },
      { key: "apertura_pct", label: "Tasa de apertura", help: "Abrieron el 2º formulario / mensaje enviado.", kind: "pct", stagesOnly: true, better: "up" },
      { key: "aperturas_totales", label: "Aperturas totales del enlace", help: "Una persona puede abrir el enlace varias veces.", kind: "count", stagesOnly: true },
      { key: "completan_si_abren_pct", label: "Completan si abren", help: "Completaron el 2º formulario / lo abrieron.", kind: "pct", stagesOnly: true, better: "up" },
      { key: "rebote_etapa_2_pct", label: "Rebote del 2º formulario", help: "Abrieron el 2º formulario y no lo completaron / lo abrieron.", kind: "pct", stagesOnly: true, better: "down" },
      { key: "invitacion_expirada", label: "Invitaciones expiradas", help: "El enlace venció sin completarse.", kind: "count", stagesOnly: true },
      { key: "recordatorios_enviados", label: "Recordatorios enviados", help: "Personas que recibieron el recordatorio.", kind: "count", stagesOnly: true },
      { key: "rescatados_por_recordatorio", label: "Rescatados por el recordatorio", help: "Completaron después de recibir el recordatorio.", kind: "count", stagesOnly: true },
    ],
  },
  {
    title: "Tiempos y uso posterior",
    description: "¿Cuánto se demoran y sirven las postulaciones a los reclutadores?",
    rows: [
      { key: "etapa1_mediana_min", label: "Llenar el primer paso (mediana)", help: "Minutos desde que se mostró el formulario hasta enviarlo, en la sesión que envió la etapa 1.", kind: "min", better: "down" },
      { key: "entre_etapas_mediana_min", label: "Espera entre etapas (mediana)", help: "Desde enviar la etapa 1 hasta abrir la etapa 2 (retraso + reacción).", kind: "min", stagesOnly: true, better: "down" },
      { key: "etapa2_mediana_min", label: "Llenar el 2º formulario (mediana)", help: "Minutos desde que abrió el enlace hasta enviar, en la sesión que envió la etapa 2.", kind: "min", stagesOnly: true, better: "down" },
      { key: "llenado_mediana_min", label: "Llenando, sin espera (mediana)", help: "Solo completas: tiempo del paso 1 + tiempo del paso 2, sin contar la espera del WhatsApp ni la reacción.", kind: "min", better: "down" },
      { key: "total_mediana_min", label: "Total hasta quedar completa, con espera (mediana)", help: "Desde que vio el formulario hasta quedar completa, contando la espera del WhatsApp.", kind: "min", better: "down" },
      { key: "asignadas_a_proceso", label: "Asignadas a un proceso", help: "Postulaciones que un reclutador ya asignó a un proceso.", kind: "count" },
      { key: "asignadas_a_proceso_pct", label: "Tasa de asignación a proceso", help: "Asignadas a proceso / postularon.", kind: "pct", better: "up" },
      { key: "parciales_asignadas_a_proceso", label: "Parciales asignadas a proceso", help: "Postulaciones incompletas que igual se asignaron.", kind: "count", stagesOnly: true },
    ],
  },
];

// Camino del postulante, en orden. Los pasos stagesOnly no existen en el formulario completo.
interface PathStep {
  key: string;
  label: string;
  hint: string;
  stagesOnly?: boolean;
}

const PATH_STEPS: PathStep[] = [
  { key: "asignadas", label: "Entraron al experimento", hint: "Asignadas por el sorteo" },
  { key: "vieron_formulario", label: "Vieron el formulario", hint: "Se les mostró el formulario" },
  { key: "intentaron_enviar", label: "Intentaron enviar", hint: "Presionaron enviar" },
  { key: "postularon", label: "Postularon", hint: "Enviaron el primer paso" },
  { key: "solo_primer_paso", label: "Les faltó el 2º paso", hint: "Enviaron solo la etapa 1", stagesOnly: true },
  { key: "mensaje_enviado", label: "Recibieron el WhatsApp", hint: "Mensaje enviado con el enlace", stagesOnly: true },
  { key: "abrieron_segundo_formulario", label: "Abrieron el 2º formulario", hint: "Entraron al enlace", stagesOnly: true },
  { key: "completaron_etapa_2", label: "Completaron el 2º formulario", hint: "Enviaron la etapa 2", stagesOnly: true },
];

// Pasos con duración por persona (step-times), para la distribución de tiempos.
interface TimeStep {
  key: string;
  label: string;
  hint: string;
  stagesOnly?: boolean;
}

const TIME_STEPS: TimeStep[] = [
  { key: "OTP", label: "Código OTP", hint: "Desde que se envía el código hasta que la persona lo verifica (solo quienes ya existían en la base)." },
  { key: "ETAPA_1", label: "Paso 1 / formulario", hint: "Desde que se muestra el formulario hasta enviarlo, en la sesión que envió el primer paso." },
  { key: "ESPERA", label: "Espera entre pasos", hint: "Desde enviar el paso 1 hasta abrir el paso 2: espera del WhatsApp + reacción.", stagesOnly: true },
  { key: "ETAPA_2", label: "Paso 2", hint: "Desde que abre el enlace hasta enviar el segundo paso.", stagesOnly: true },
  { key: "LLENADO", label: "Llenando, sin espera", hint: "Solo completas: tiempo del paso 1 + paso 2, sin la espera del WhatsApp." },
  { key: "TOTAL", label: "Total hasta completa", hint: "Solo completas: desde que vio el formulario hasta quedar completa, con espera." },
];

// Anchos de intervalo "redondos" para el histograma, en segundos.
const BIN_WIDTHS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 43200, 86400];
const MAX_BINS = 30;
const ALL_SERIES = "TODOS";

type SubTab = "resumen" | "camino" | "otp" | "campos" | "detalle" | "diagnostico";

const SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: "resumen", label: "Resumen" },
  { key: "camino", label: "Camino del postulante" },
  { key: "otp", label: "Código OTP" },
  { key: "campos", label: "Tiempo por campo" },
  { key: "detalle", label: "Detalle" },
  { key: "diagnostico", label: "Diagnóstico" },
];

// ─── types ────────────────────────────────────────────────────────────────────

interface ExperimentVariant {
  code: string;
  name: string;
  weight: number;
  isControl: boolean;
  stageCount: number;
  mode?: string;
}

interface Experiment {
  experimentId: string;
  code: string;
  name: string;
  status: string;
  trafficPercentage: number;
  completionWindowHours: number;
  minimumSamplePerVariant: number | null;
  startedAt: string | null;
  endedAt: string | null;
  description?: string | null;
  hypothesis?: string | null;
  primaryMetric?: string;
  conclusion?: string | null;
  enrolledForms: number;
  variants: ExperimentVariant[];
}

type Row = Record<string, unknown>;

interface MetricsData {
  overview: Row[];
  forms: Row[];
  daily: Row[];
  segments: Row[];
  exposure: Row[];
  primary: Row[];
  funnel: Row[];
  timeBreakdown: Row[];
  fields: Row[];
  stepTimes: Row[];
  otp: Row[];
  otpPending: Row[];
  delayEffect: Row[];
  srm: Row[];
  health: Row[];
}

// Métricas que pueden faltar sin romper el panel (p.ej., backend aún sin desplegar): vacías si fallan.
const OPTIONAL_METRIC_REQUESTS: Array<[keyof MetricsData, string]> = [
  ["fields", "fields"],
  ["stepTimes", "step-times"],
  ["otp", "otp"],
  ["otpPending", "otp-pending"],
];

const METRIC_REQUESTS: Array<[keyof MetricsData, string]> = [
  ["overview", "overview"],
  ["forms", "forms"],
  ["daily", "daily"],
  ["segments", "segments"],
  ["exposure", "exposure"],
  ["primary", "primary"],
  ["funnel", "funnel"],
  ["timeBreakdown", "time-breakdown"],
  ["delayEffect", "delay-effect"],
  ["srm", "srm"],
];

// ─── helpers ──────────────────────────────────────────────────────────────────

// pg devuelve bigint/numeric como string.
const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const fmtCount = (value: unknown): string => {
  const n = num(value);
  return n === null ? "—" : n.toLocaleString("es-CL");
};

const fmtPct = (value: unknown): string => {
  const n = num(value);
  return n === null ? "—" : `${n.toLocaleString("es-CL", { maximumFractionDigits: 1 })} %`;
};

const fmtMin = (value: unknown): string => {
  const n = num(value);
  if (n === null) return "—";
  if (n >= 120) return `${(n / 60).toLocaleString("es-CL", { maximumFractionDigits: 1 })} h`;
  return `${n.toLocaleString("es-CL", { maximumFractionDigits: 1 })} min`;
};

const fmtValue = (kind: MetricKind, value: unknown): string =>
  kind === "pct" ? fmtPct(value) : kind === "min" ? fmtMin(value) : fmtCount(value);

const pctOf = (part: number, whole: number): number | null => (whole > 0 ? Math.round((1000 * part) / whole) / 10 : null);

const fmtSec = (value: unknown): string => {
  const n = num(value);
  if (n === null) return "—";
  if (n >= 7200) return `${(n / 3600).toLocaleString("es-CL", { maximumFractionDigits: 1 })} h`;
  if (n >= 90) return `${(n / 60).toLocaleString("es-CL", { maximumFractionDigits: 1 })} min`;
  return `${n.toLocaleString("es-CL", { maximumFractionDigits: 1 })} s`;
};

const fmtDate = (value: unknown, withTime = false): string => {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("es-CL", withTime
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Fecha "YYYY-MM-DD" (sin zona) tal como la entrega la cohorte diaria.
const fmtDay = (value: unknown): string => {
  const text = String(value ?? "").slice(0, 10);
  const [y, m, d] = text.split("-");
  return y && m && d ? `${d}-${m}` : text;
};

const daysSince = (value: string | null): number | null => {
  if (!value) return null;
  const ms = Date.now() - new Date(value).getTime();
  return ms >= 0 ? Math.floor(ms / 86400000) : null;
};

// Diferencia contra el control: pp para tasas, unidades para el resto. El color dice si es mejor o peor.
const delta = (kind: MetricKind, value: unknown, control: unknown, better?: "up" | "down"): { text: string; color: string } | null => {
  const a = num(value);
  const b = num(control);
  if (a === null || b === null) return null;
  const diff = Math.round((a - b) * 10) / 10;
  if (diff === 0) return { text: "igual", color: MUTED };
  const sign = diff > 0 ? "+" : "−";
  const abs = Math.abs(diff).toLocaleString("es-CL", { maximumFractionDigits: 1 });
  const text = kind === "pct" ? `${sign}${abs} pp` : kind === "min" ? `${sign}${abs} min` : `${sign}${abs}`;
  const color = !better ? MUTED : (diff > 0) === (better === "up") ? GOOD : BAD;
  return { text, color };
};

const withAlpha = (hex: string, alpha: number): string => {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

const quantile = (sorted: number[], q: number): number | null => {
  if (sorted.length === 0) return null;
  const pos = (sorted.length - 1) * q;
  const low = Math.floor(pos);
  const high = Math.ceil(pos);
  return sorted[low] + (sorted[high] - sorted[low]) * (pos - low);
};

// Ancho de intervalo para que el p95 quepa en ≤ MAX_BINS barras; el resto queda "fuera del gráfico".
const binWidthFor = (limit: number): number =>
  BIN_WIDTHS.find((width) => Math.ceil(limit / width) <= MAX_BINS) ?? BIN_WIDTHS[BIN_WIDTHS.length - 1];

// Descarga una tabla como CSV (separador ";" y BOM para que Excel en español lo abra bien).
const downloadCsv = (filename: string, header: string[], rows: Array<Array<unknown>>) => {
  const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const text = [header, ...rows].map((row) => row.map(cell).join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── sub-components ───────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
  border: "1px solid #eef2f7",
};

const th: React.CSSProperties = {
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: MUTED,
  padding: "10px 12px",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  fontSize: 13,
  color: INK,
  padding: "10px 12px",
  borderBottom: "1px solid #f1f5f9",
};

const tdNum: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

const Heading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div style={{ marginBottom: 12 }}>
    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: INK }}>{title}</h2>
    {subtitle && <p style={{ margin: "2px 0 0", fontSize: 13, color: MUTED }}>{subtitle}</p>}
  </div>
);

const Block = ({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) => (
  <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} style={{ marginBottom: 32 }}>
    <Heading title={title} subtitle={subtitle} />
    {children}
  </motion.section>
);

const Chip = ({ label, color, bg }: { label: string; color: string; bg: string }) => (
  <span style={{ display: "inline-block", fontSize: 12, fontWeight: 600, color, background: bg, borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>
    {label}
  </span>
);

const Empty = ({ message }: { message: string }) => (
  <div style={{ ...card, textAlign: "center", color: "#94a3b8", fontSize: 14, padding: "28px 20px" }}>{message}</div>
);

const TableWrap = ({ children }: { children: React.ReactNode }) => (
  <div style={{ ...card, padding: 0, overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>{children}</table>
  </div>
);

const Help = ({ text }: { text: string }) => (
  <span title={text} style={{ cursor: "help", color: "#94a3b8", fontSize: 12, marginLeft: 6 }}>ⓘ</span>
);

const VariantDot = ({ color }: { color: string }) => (
  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: color, marginRight: 8, flexShrink: 0 }} />
);

// ─── main component ───────────────────────────────────────────────────────────

const PanelExperimentoAB: React.FC = () => {
  const base = process.env.REACT_APP_API_APPLICANT ?? "";

  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [includeForced, setIncludeForced] = useState(false);
  const [subTab, setSubTab] = useState<SubTab>("resumen");
  const [fieldVariant, setFieldVariant] = useState("");
  const [fieldSort, setFieldSort] = useState<FieldSort>("mediana");
  const [timeStep, setTimeStep] = useState("ETAPA_1");
  const [timeAsShare, setTimeAsShare] = useState(false);
  const [otpFilter, setOtpFilter] = useState<"todos" | "no_verificaron" | "verificaron">("todos");
  const [data, setData] = useState<MetricsData | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  // Pantallas angostas: el embudo se apila por variante en vez de ir lado a lado.
  const [narrow, setNarrow] = useState(() => window.matchMedia?.("(max-width: 640px)").matches ?? false);

  useEffect(() => {
    const query = window.matchMedia?.("(max-width: 640px)");
    if (!query) return;
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${base}/experiment/list`);
        const body = res.ok ? await res.json() : { experiments: [] };
        if (cancelled) return;
        const list: Experiment[] = body.experiments ?? [];
        setExperiments(list);
        setSelectedCode((current) => current || list.find((item) => item.status === "ACTIVO")?.code || list[0]?.code || "");
        if (!res.ok) setError("No se pudo cargar la lista de experimentos.");
      } catch {
        if (!cancelled) setError("No se pudo cargar la lista de experimentos.");
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base]);

  const loadMetrics = useCallback(async () => {
    if (!selectedCode) return;
    setLoading(true);
    setError("");
    const fetchRows = async (type: string, code?: string): Promise<Row[]> => {
      const path = code ? `/${encodeURIComponent(code)}` : "";
      const res = await fetch(`${base}/experiment/metrics${path}?type=${type}&includeForced=${includeForced}`);
      if (!res.ok) throw new Error(type);
      const body = await res.json();
      return body.rows ?? [];
    };
    try {
      const [results, optional, health] = await Promise.all([
        Promise.all(METRIC_REQUESTS.map(([, type]) => fetchRows(type, selectedCode))),
        Promise.all(OPTIONAL_METRIC_REQUESTS.map(([, type]) => fetchRows(type, selectedCode).catch(() => [] as Row[]))),
        fetchRows("health").catch(() => [] as Row[]),
      ]);
      const next = { health } as MetricsData;
      METRIC_REQUESTS.forEach(([key], index) => {
        next[key] = results[index];
      });
      OPTIONAL_METRIC_REQUESTS.forEach(([key], index) => {
        next[key] = optional[index];
      });
      setData(next);
      setUpdatedAt(new Date());
    } catch {
      setError("No se pudieron cargar las métricas del experimento.");
    } finally {
      setLoading(false);
    }
  }, [base, selectedCode, includeForced]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  const experiment = experiments.find((item) => item.code === selectedCode);

  // Variantes: control primero, luego por código (mismo orden en todas las vistas).
  const variants = useMemo<ExperimentVariant[]>(() => {
    const fromList = experiment?.variants ?? [];
    const fromData = (data?.overview ?? [])
      .map((row) => String(row.variante))
      .filter((code) => code !== "TOTAL" && !fromList.some((variant) => variant.code === code))
      .map((code) => ({ code, name: code, weight: 0, isControl: false, stageCount: 1 }));
    return [...fromList, ...fromData].sort((a, b) => Number(b.isControl) - Number(a.isControl) || a.code.localeCompare(b.code));
  }, [experiment, data]);

  const colorOf = (code: string): string => {
    const index = variants.findIndex((variant) => variant.code === code);
    return VARIANT_COLORS[(index >= 0 ? index : 0) % VARIANT_COLORS.length];
  };
  const nameOf = (code: string): string => variants.find((variant) => variant.code === code)?.name ?? code;

  const overviewBy = useMemo(() => {
    const map: Record<string, Row> = {};
    (data?.overview ?? []).forEach((row) => {
      map[String(row.variante)] = row;
    });
    return map;
  }, [data]);

  const control = variants.find((variant) => variant.isControl);
  const controlRow = control ? overviewBy[control.code] : undefined;
  const isStagesVariant = (code: string): boolean =>
    (overviewBy[code]?.modo ?? variants.find((variant) => variant.code === code)?.mode) === "ETAPAS";
  const anyStages = variants.some((variant) => isStagesVariant(variant.code));
  // Variante sin asignaciones todavía: sus conteos son 0, no "sin dato".
  const valueOf = (code: string, key: string, kind: MetricKind = "count"): unknown => {
    const row = overviewBy[code];
    return row ? row[key] : kind === "count" ? 0 : null;
  };
  const countOf = (code: string, key: string): number => num(valueOf(code, key)) ?? 0;

  // ── forms pivot ────────────────────────────────────────────────────────────
  const forms = useMemo(() => {
    const map = new Map<string, { info: Row; byVariant: Record<string, Row> }>();
    (data?.forms ?? []).forEach((row) => {
      const id = String(row.ContextoFormularioId);
      const entry = map.get(id) ?? { info: row, byVariant: {} };
      if (row.variante) entry.byVariant[String(row.variante)] = row;
      map.set(id, entry);
    });
    return Array.from(map.values());
  }, [data]);

  // ── views ──────────────────────────────────────────────────────────────────

  // Tiempo de cada paso (sesión que lo envió): "Paso 1 · Paso 2" en etapas, un solo paso en el completo.
  const renderStepTimes = (code: string) => {
    const steps = isStagesVariant(code)
      ? [
        { label: "Paso 1", key: "etapa1_mediana_min", diff: true },
        { label: "Paso 2", key: "etapa2_mediana_min", diff: false },
      ]
      : [{ label: "Paso único", key: "etapa1_mediana_min", diff: false }];
    const isControl = variants.find((variant) => variant.code === code)?.isControl ?? false;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 16px", marginTop: 2 }}>
        {steps.map((step) => {
          const value = valueOf(code, step.key, "min");
          const diff = step.diff && !isControl ? delta("min", value, controlRow?.etapa1_mediana_min, "down") : null;
          return (
            <div key={step.key} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 12, color: MUTED }}>{step.label}</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: INK }}>{fmtMin(value)}</span>
              {diff && <span style={{ fontSize: 12, fontWeight: 700, color: diff.color }}>{diff.text}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  // Tarjeta por variante con los números que más pesan en la decisión.
  const renderScorecards = () => {
    const kpis: Array<{
      key: string;
      label: string;
      kind: MetricKind;
      better?: "up" | "down";
      sub?: (code: string) => string;
      custom?: (code: string) => React.ReactNode;
      // Solo existe en variantes por etapas: en el formulario completo se muestra "No aplica".
      stagesOnly?: boolean;
    }> = [
      {
        key: "conversion_pct",
        label: "Tasa de conversión",
        kind: "pct",
        better: "up",
        sub: (code) => `${fmtCount(valueOf(code, "postularon"))} de ${fmtCount(valueOf(code, "asignadas"))} personas postularon${isStagesVariant(code) ? " (al menos el paso 1)" : ""}`,
      },
      {
        key: "completas_pct",
        label: "Conversión a postulación completa",
        kind: "pct",
        better: "up",
        sub: (code) => `${fmtCount(valueOf(code, "completas"))} de ${fmtCount(valueOf(code, "asignadas"))} personas quedaron completas`,
      },
      {
        key: "conversion_vio_pct",
        label: "Conversión de quienes vieron el formulario",
        kind: "pct",
        better: "up",
        sub: (code) => `${fmtCount(valueOf(code, "postularon"))} de ${fmtCount(valueOf(code, "vieron_formulario"))} que lo vieron postularon`,
      },
      {
        key: "conversion_entre_etapas_pct",
        label: "Conversión del paso 1 al paso 2",
        kind: "pct",
        better: "up",
        stagesOnly: true,
        sub: (code) => (isStagesVariant(code)
          ? `${fmtCount(valueOf(code, "completaron_etapa_2"))} de ${fmtCount(valueOf(code, "solo_primer_paso"))} que necesitaban el paso 2`
          : "postula todo en un solo paso"),
      },
      { key: "rebote_pct", label: "Rebote", kind: "pct", better: "down", sub: (code) => `${fmtCount(valueOf(code, "rebotaron"))} vieron y se fueron sin intentar` },
      {
        key: "tiempo_por_paso",
        label: "Tiempo por paso",
        kind: "min",
        custom: renderStepTimes,
        sub: (code) => (isStagesVariant(code) ? "mediana de cada paso; paso 1 vs el formulario del control" : "mediana, desde que lo ve hasta enviar"),
      },
      {
        key: "llenado_mediana_min",
        label: "Tiempo llenando (sin espera)",
        kind: "min",
        better: "down",
        sub: (code) => (isStagesVariant(code) ? "mediana de completas, paso 1 + paso 2" : "mediana de completas"),
      },
      {
        key: "total_mediana_min",
        label: "Tiempo hasta completa (con espera)",
        kind: "min",
        better: "down",
        sub: (code) => (isStagesVariant(code) ? "mediana, incluye la espera del WhatsApp" : "mediana, desde que vio el formulario"),
      },
    ];
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(300px, 100%), 1fr))`, gap: 16 }}>
        {variants.map((variant) => {
          const color = colorOf(variant.code);
          return (
            <div key={variant.code} style={{ ...card, borderTop: `4px solid ${color}`, padding: 0 }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>{nameOf(variant.code)}</div>
                  <div style={{ fontSize: 12, color: MUTED }}>
                    {variant.isControl ? "Control" : "Variante"} · {isStagesVariant(variant.code) ? "por etapas" : "formulario completo"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: INK }}>{fmtCount(valueOf(variant.code, "asignadas"))}</div>
                  <div style={{ fontSize: 12, color: MUTED }}>personas · {fmtPct(valueOf(variant.code, "distribucion_pct", "pct"))} del total</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                {kpis.map((kpi, index) => {
                  const value = valueOf(variant.code, kpi.key, kpi.kind);
                  const notApplicable = kpi.stagesOnly && !isStagesVariant(variant.code);
                  const diff = !variant.isControl && !kpi.custom ? delta(kpi.kind, value, controlRow?.[kpi.key], kpi.better) : null;
                  return (
                    <div
                      key={kpi.key}
                      data-kpi={kpi.key}
                      style={{
                        padding: "14px 20px",
                        borderRight: index % 2 === 0 ? "1px solid #f1f5f9" : undefined,
                        borderBottom: index < kpis.length - 2 ? "1px solid #f1f5f9" : undefined,
                      }}
                    >
                      <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{kpi.label}</div>
                      {notApplicable ? (
                        <div style={{ fontSize: 20, fontWeight: 600, color: "#cbd5e1", marginTop: 4 }}>No aplica</div>
                      ) : kpi.custom ? kpi.custom(variant.code) : (
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
                          <span style={{ fontSize: 26, fontWeight: 700, color: INK }}>{fmtValue(kpi.kind, value)}</span>
                          {diff && <span style={{ fontSize: 13, fontWeight: 700, color: diff.color }}>{diff.text}</span>}
                        </div>
                      )}
                      {kpi.sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{kpi.sub(variant.code)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Dónde está hoy cada persona asignada: suma exacta de asignadas.
  const renderCurrentState = () => {
    // Las tres primeras de "etapa 1" suman en_etapa_1: postularon, no han abierto el 2º paso.
    const segments = [
      { key: "no_postularon", label: "No postularon", color: "#cbd5e1", stagesOnly: false },
      { key: "esperando_whatsapp", label: "Esperando el WhatsApp del 2º paso", color: "#fde047", stagesOnly: true },
      { key: "whatsapp_sin_abrir", label: "WhatsApp enviado, sin abrir", color: "#fb923c", stagesOnly: true },
      { key: "sin_whatsapp", label: "Sin WhatsApp (error o sin invitación)", color: "#f87171", stagesOnly: true },
      { key: "en_etapa_2", label: "Abrieron el 2º paso, sin completar", color: "#a78bfa", stagesOnly: true },
      { key: "completas", label: "Completas", color: "#22c55e", stagesOnly: false },
    ];
    const valuesOf = (code: string): Record<string, number> => {
      const total = countOf(code, "asignadas");
      return {
        no_postularon: Math.max(total - countOf(code, "postularon"), 0),
        esperando_whatsapp: countOf(code, "esperando_whatsapp"),
        whatsapp_sin_abrir: countOf(code, "whatsapp_sin_abrir"),
        sin_whatsapp: countOf(code, "sin_whatsapp"),
        en_etapa_2: countOf(code, "en_etapa_2"),
        completas: countOf(code, "completas"),
      };
    };
    // "Sin WhatsApp" solo aparece en la leyenda si alguien cayó ahí.
    const legend = segments.filter((segment) =>
      (!segment.stagesOnly || anyStages)
      && (segment.key !== "sin_whatsapp" || variants.some((variant) => countOf(variant.code, "sin_whatsapp") > 0)));

    // Conteos explícitos bajo cada barra: % sobre las asignadas.
    const stat = (label: string, value: number, total: number, sub?: string) => (
      <div style={{ flex: "1 1 150px", minWidth: 0 }}>
        <div style={{ fontSize: 12, color: MUTED }}>{label}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: INK }}>{fmtCount(value)}</span>
          <span style={{ fontSize: 12, color: MUTED }}>{fmtPct(pctOf(value, total))}</span>
        </div>
        {sub && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>}
      </div>
    );

    return (
      <div style={card}>
        {variants.map((variant) => {
          const total = countOf(variant.code, "asignadas");
          const values = valuesOf(variant.code);
          const stages = isStagesVariant(variant.code);
          const waiting = values.esperando_whatsapp;
          return (
            <div key={variant.code} data-state={variant.code} style={{ marginBottom: 22 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                <span style={{ display: "flex", alignItems: "center", fontSize: 14, fontWeight: 600, color: INK }}>
                  <VariantDot color={colorOf(variant.code)} />
                  {nameOf(variant.code)}
                </span>
                <span style={{ fontSize: 12, color: MUTED }}>
                  {fmtCount(total)} personas{stages && waiting > 0 ? ` · ${fmtCount(waiting)} esperando el WhatsApp` : ""}
                </span>
              </div>
              <div style={{ display: "flex", height: 30, borderRadius: 8, overflow: "hidden", background: "#f1f5f9" }}>
                {total > 0 && segments.map((segment) => {
                  const value = values[segment.key];
                  if (!value) return null;
                  return (
                    <div
                      key={segment.key}
                      title={`${segment.label}: ${value}`}
                      style={{ width: `${(100 * value) / total}%`, background: segment.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: INK, minWidth: 24 }}
                    >
                      {value}
                    </div>
                  );
                })}
              </div>
              <div data-state-counts={variant.code} style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 10 }}>
                {stages ? (
                  <>
                    {stat("Completaron el 1er paso", countOf(variant.code, "postularon"), total, "postularon")}
                    {stat(
                      "Completaron el 2º paso",
                      countOf(variant.code, "completaron_etapa_2"),
                      total,
                      `de ${fmtCount(countOf(variant.code, "solo_primer_paso"))} que lo necesitaban`,
                    )}
                    {stat(
                      "Completaron todo",
                      values.completas,
                      total,
                      `${fmtCount(countOf(variant.code, "completaron_en_primer_paso"))} en el 1er paso + ${fmtCount(countOf(variant.code, "completaron_etapa_2"))} con el 2º`,
                    )}
                  </>
                ) : (
                  <>
                    {stat("Enviaron el formulario", countOf(variant.code, "postularon"), total, "un solo paso")}
                    {stat("Completaron todo", values.completas, total)}
                  </>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: MUTED }}>
          {legend.map((segment) => (
            <span key={segment.key} style={{ display: "flex", alignItems: "center" }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: segment.color, marginRight: 6 }} />
              {segment.label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Embudo del camino: una columna por variante, filas alineadas por paso.
  const renderPath = () => {
    const steps = PATH_STEPS.filter((step) => !step.stagesOnly || anyStages);

    // Texto entre dos pasos: cuántos siguen y qué pasó con los que no.
    const connector = (code: string, prev: PathStep, step: PathStep): React.ReactNode => {
      const from = countOf(code, prev.key);
      const to = countOf(code, step.key);
      const lost = Math.max(from - to, 0);
      const keep = pctOf(to, from);
      if (from === 0) return null;
      if (step.key === "solo_primer_paso") {
        const done = countOf(code, "completaron_en_primer_paso");
        return (
          <>
            <span style={{ color: MUTED }}>↓ {fmtCount(to)} necesitan el 2º paso</span>
            {done > 0 && <span style={{ color: GOOD, fontWeight: 600 }}> · ✓ {fmtCount(done)} completaron todo en el 1º</span>}
          </>
        );
      }
      if (step.key === "mensaje_enviado") {
        const pending = countOf(code, "mensaje_pendiente");
        const failed = Math.max(lost - pending, 0);
        return (
          <>
            <span style={{ color: MUTED }}>↓ {keep === null ? "—" : `${keep.toLocaleString("es-CL")} %`} con mensaje</span>
            {pending > 0 && <span style={{ color: "#b45309", fontWeight: 600 }}> · {fmtCount(pending)} esperando envío</span>}
            {failed > 0 && <span style={{ color: BAD, fontWeight: 600 }}> · {fmtCount(failed)} sin mensaje</span>}
          </>
        );
      }
      const reason = step.key === "intentaron_enviar" ? "rebotaron" : "se fueron";
      return (
        <>
          <span style={{ color: MUTED }}>↓ {keep === null ? "—" : `${keep.toLocaleString("es-CL")} %`} sigue</span>
          {lost > 0 && <span style={{ color: BAD, fontWeight: 600 }}> · −{fmtCount(lost)} {reason}</span>}
        </>
      );
    };

    // Una columna por variante; en pantallas angostas se apila un embudo por variante.
    const pathGrid = (cols: ExperimentVariant[], compact: boolean) => {
      const colSteps = steps.filter((step) => !step.stagesOnly || cols.some((col) => isStagesVariant(col.code)));
      const gridColumns = compact
        ? "minmax(110px, 140px) minmax(0, 1fr)"
        : `minmax(170px, 220px) repeat(${cols.length}, minmax(200px, 1fr))`;
      return (
        <div style={{ ...card, overflowX: "auto" }}>
          <div style={{ minWidth: compact ? 0 : 170 + 200 * cols.length }}>
            <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 20px", marginBottom: 8 }}>
              <div />
              {cols.map((variant) => (
                <div key={variant.code} style={{ display: "flex", alignItems: "center", fontSize: 14, fontWeight: 700, color: INK }}>
                  <VariantDot color={colorOf(variant.code)} />
                  {nameOf(variant.code)}
                </div>
              ))}
            </div>

            {colSteps.map((step, index) => (
              <React.Fragment key={step.key}>
                {index > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 20px", padding: "6px 0" }}>
                    <div />
                    {cols.map((variant) => (
                      <div key={variant.code} style={{ fontSize: 12, paddingLeft: 10, borderLeft: "2px dashed #e2e8f0" }}>
                        {step.stagesOnly && !isStagesVariant(variant.code) ? null : connector(variant.code, colSteps[index - 1], step)}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 20px", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>{step.label}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{step.hint}</div>
                  </div>
                  {cols.map((variant) => {
                    if (step.stagesOnly && !isStagesVariant(variant.code)) {
                      return (
                        <div key={variant.code} style={{ height: 38, borderRadius: 8, border: "1px dashed #e2e8f0", display: "flex", alignItems: "center", paddingLeft: 12, fontSize: 12, color: "#94a3b8" }}>
                          No aplica: postula todo en un paso
                        </div>
                      );
                    }
                    const total = countOf(variant.code, "asignadas");
                    const value = countOf(variant.code, step.key);
                    const share = pctOf(value, total);
                    return (
                      <div key={variant.code} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ flex: 1, height: 38, borderRadius: 8, background: "#f1f5f9", position: "relative", overflow: "hidden" }}>
                          <div style={{ width: `${total ? Math.max((100 * value) / total, value ? 3 : 0) : 0}%`, height: "100%", background: colorOf(variant.code), opacity: 0.9, borderRadius: 8, transition: "width .4s" }} />
                          <span style={{ position: "absolute", left: 12, top: 0, bottom: 0, display: "flex", alignItems: "center", fontSize: 15, fontWeight: 700, color: value && total && value / total > 0.18 ? "#fff" : INK }}>
                            {fmtCount(value)}
                          </span>
                        </div>
                        <span style={{ width: 54, textAlign: "right", fontSize: 13, fontWeight: 600, color: MUTED }}>{share === null ? "—" : `${share.toLocaleString("es-CL")} %`}</span>
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            ))}

            {/* Resultado final: completas, venga de donde venga. */}
            <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 20px", alignItems: "center", marginTop: 18, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: GOOD, display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckCircleIcon style={{ fontSize: 18 }} /> Postulación completa
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>Resultado final del camino</div>
              </div>
              {cols.map((variant) => {
                const total = countOf(variant.code, "asignadas");
                const done = countOf(variant.code, "completas");
                const firstStep = countOf(variant.code, "completaron_en_primer_paso");
                const secondStep = countOf(variant.code, "completaron_etapa_2");
                const share = pctOf(done, total);
                return (
                  <div key={variant.code}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontSize: 24, fontWeight: 700, color: INK }}>{fmtCount(done)}</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: GOOD }}>{share === null ? "—" : `${share.toLocaleString("es-CL")} %`} de los que entraron</span>
                    </div>
                    <div style={{ fontSize: 12, color: MUTED }}>
                      {isStagesVariant(variant.code)
                        ? `${fmtCount(firstStep)} en el 1º paso + ${fmtCount(secondStep)} con el 2º formulario`
                        : "todo en un solo paso"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    };

    return narrow ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {variants.map((variant) => <React.Fragment key={variant.code}>{pathGrid([variant], true)}</React.Fragment>)}
      </div>
    ) : pathGrid(variants, false);
  };

  // Tiempos del camino como una línea: paso 1 → espera → paso 2 = total.
  const renderTimeline = () => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 16 }}>
      {variants.map((variant) => {
        const stages = isStagesVariant(variant.code);
        const parts = stages
          ? [
            { label: "Llenar paso 1", value: valueOf(variant.code, "etapa1_mediana_min", "min") },
            { label: "Espera + reacción", value: valueOf(variant.code, "entre_etapas_mediana_min", "min") },
            { label: "Llenar paso 2", value: valueOf(variant.code, "etapa2_mediana_min", "min") },
          ]
          : [{ label: "Llenar el formulario", value: valueOf(variant.code, "etapa1_mediana_min", "min") }];
        return (
          <div key={variant.code} style={{ ...card, borderLeft: `4px solid ${colorOf(variant.code)}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 10 }}>{nameOf(variant.code)}</div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {parts.map((part, index) => (
                <React.Fragment key={part.label}>
                  {index > 0 && <span style={{ color: "#cbd5e1" }}>→</span>}
                  <div style={{ background: "#f8fafc", borderRadius: 8, padding: "6px 10px" }}>
                    <div style={{ fontSize: 11, color: MUTED }}>{part.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{fmtMin(part.value)}</div>
                  </div>
                </React.Fragment>
              ))}
              <span style={{ color: "#cbd5e1" }}>=</span>
              <div style={{ background: "#ecfdf5", borderRadius: 8, padding: "6px 10px" }}>
                <div style={{ fontSize: 11, color: GOOD }}>Total hasta completa</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: GOOD }}>{fmtMin(valueOf(variant.code, "total_mediana_min", "min"))}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: 12, color: MUTED }}>Llenando, sin espera{stages ? " (paso 1 + paso 2)" : ""}:</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>{fmtMin(valueOf(variant.code, "llenado_mediana_min", "min"))}</span>
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>Medianas: las partes no siempre suman el total.</div>
          </div>
        );
      })}
    </div>
  );

  // Comparación métrica por métrica, agrupada; las tasas llevan una barra para leerlas de un vistazo.
  const renderDetail = () => {
    const gridColumns = `minmax(220px, 1.4fr) repeat(${variants.length}, minmax(min(150px, 100%), 1fr))`;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {METRIC_SECTIONS.map((section) => {
          const rows = section.rows.filter((row) => !row.stagesOnly || anyStages);
          if (rows.length === 0) return null;
          return (
            <div key={section.title} style={{ ...card, padding: 0, overflowX: "auto" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{section.title}</div>
                <div style={{ fontSize: 12, color: MUTED }}>{section.description}</div>
              </div>
              <div style={{ minWidth: 220 + 150 * variants.length }}>
                <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 16, padding: "8px 20px", background: "#f8fafc" }}>
                  <div />
                  {variants.map((variant) => (
                    <div key={variant.code} style={{ display: "flex", alignItems: "center", fontSize: 12, fontWeight: 700, color: colorOf(variant.code) }}>
                      <VariantDot color={colorOf(variant.code)} />
                      {nameOf(variant.code)}
                    </div>
                  ))}
                </div>
                {rows.map((metric) => (
                  <div key={metric.key} data-metric={metric.key} style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 16, padding: "10px 20px", borderTop: "1px solid #f1f5f9", alignItems: "center" }}>
                    <div style={{ fontSize: 13, color: "#334155" }}>
                      {metric.label}
                      <Help text={metric.help} />
                    </div>
                    {variants.map((variant) => {
                      if (metric.stagesOnly && !isStagesVariant(variant.code)) {
                        return <div key={variant.code} style={{ fontSize: 12, color: "#cbd5e1" }}>No aplica</div>;
                      }
                      const value = valueOf(variant.code, metric.key, metric.kind);
                      const controlApplies = !metric.stagesOnly || (control ? isStagesVariant(control.code) : false);
                      const diff = !variant.isControl && controlApplies && metric.key !== "distribucion_pct"
                        ? delta(metric.kind, value, controlRow?.[metric.key], metric.better)
                        : null;
                      const pct = metric.kind === "pct" ? num(value) : null;
                      return (
                        <div key={variant.code}>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" }}>{fmtValue(metric.kind, value)}</span>
                            {diff && <span style={{ fontSize: 12, fontWeight: 600, color: diff.color }}>{diff.text}</span>}
                          </div>
                          {pct !== null && (
                            <div style={{ height: 5, borderRadius: 3, background: "#f1f5f9", marginTop: 4 }}>
                              <div style={{ width: `${Math.min(Math.max(pct, 0), 100)}%`, height: "100%", borderRadius: 3, background: colorOf(variant.code) }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Tiempo por campo de una variante: una tabla por paso, con barra relativa al campo más lento.
  const renderFields = () => {
    const rows = data?.fields ?? [];
    const code = variants.some((variant) => variant.code === fieldVariant) ? fieldVariant : variants[0]?.code ?? "";
    const ofVariant = rows.filter((row) => row.variante === code);
    const stageNumbers = Array.from(new Set(ofVariant.map((row) => num(row.etapa) ?? 1))).sort();
    const sortKey = fieldSort === "aporte" ? "aporte_seg" : fieldSort === "orden" ? "orden_mediano" : "mediana_seg";
    const sorted = (list: Row[]) => [...list].sort((a, b) => {
      const x = num(a[sortKey]);
      const y = num(b[sortKey]);
      if (x === null || y === null) return x === null ? 1 : -1;
      return fieldSort === "orden" ? x - y : y - x;
    });
    // Mismo campo en el control (en cualquier paso), para el Δ de la mediana.
    const compareControl = Boolean(control && control.code !== code);
    const controlField = (field: string): Row | undefined =>
      compareControl ? rows.find((row) => row.variante === control?.code && row.campo === field) : undefined;
    const maxMedian = Math.max(...ofVariant.map((row) => num(row.mediana_seg) ?? 0), 1);

    const toggle = (active: boolean): React.CSSProperties => ({
      border: `1px solid ${active ? "#94a3b8" : "#e2e8f0"}`,
      background: active ? "#fff" : "transparent",
      color: active ? INK : MUTED,
      fontWeight: active ? 700 : 500,
      borderRadius: 999,
      padding: "5px 12px",
      fontSize: 13,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
    });

    return (
      <>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", marginBottom: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {variants.map((variant) => (
              <button key={variant.code} onClick={() => setFieldVariant(variant.code)} style={toggle(variant.code === code)}>
                <VariantDot color={colorOf(variant.code)} />
                {nameOf(variant.code)}
              </button>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#475569" }}>
            Ordenar por
            <select value={fieldSort} onChange={(event) => setFieldSort(event.target.value as FieldSort)} style={{ fontSize: 13, padding: "5px 8px", borderRadius: 8, border: "1px solid #cbd5e1" }}>
              {FIELD_SORTS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
            </select>
          </label>
        </div>

        {ofVariant.length === 0 ? (
          <Empty message="Aún no hay interacciones con campos registradas para esta versión." />
        ) : stageNumbers.map((stage) => {
          const list = sorted(ofVariant.filter((row) => (num(row.etapa) ?? 1) === stage));
          const people = num(list[0]?.personas_etapa) ?? 0;
          const title = isStagesVariant(code) ? `Paso ${stage}` : "Formulario completo";
          return (
            <div key={stage} data-fields-stage={stage} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: INK, margin: "0 0 8px" }}>
                {title} <span style={{ fontSize: 12, fontWeight: 400, color: MUTED }}>· {fmtCount(people)} personas tocaron al menos un campo</span>
              </div>
              <TableWrap>
                <thead>
                  <tr>
                    <th style={th}>Campo</th>
                    <th style={{ ...th, width: "30%" }}>Tiempo típico (mediana)<Help text="Mediana del tiempo con el cursor dentro del campo, entre quienes lo usaron. Si alguien entra varias veces, se suma." /></th>
                    <th style={{ ...th, textAlign: "right" }}>p75<Help text="El 75 % lo llena en este tiempo o menos." /></th>
                    {compareControl && <th style={{ ...th, textAlign: "right" }}>vs control<Help text="Diferencia de la mediana contra el mismo campo en el control. Verde: más rápido." /></th>}
                    <th style={{ ...th, textAlign: "right" }}>Peso en el paso<Help text="Tiempo total en el campo ÷ personas del paso: cuánto aporta, en promedio, al tiempo de llenar el paso. Junta lo lento con lo frecuente." /></th>
                    <th style={{ ...th, textAlign: "right" }}>Lo usaron<Help text="Personas que pusieron el cursor en el campo, sobre las que tocaron algún campo del paso." /></th>
                    <th style={{ ...th, textAlign: "right" }}>Con error<Help text="Personas a las que se les mostró un error de validación en el campo." /></th>
                    <th style={{ ...th, textAlign: "right" }}>Ediciones<Help text="Promedio de veces que se editó (cambios separados por más de medio segundo)." /></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => {
                    const field = String(row.campo);
                    const median = num(row.mediana_seg);
                    const ref = controlField(field);
                    const diff = ref ? delta("count", row.mediana_seg, ref.mediana_seg, "down") : null;
                    const errorPct = num(row.con_error_pct) ?? 0;
                    return (
                      <tr key={field} data-field={field}>
                        <td style={{ ...td, fontWeight: 600 }}>{FIELD_LABEL[field] ?? field}</td>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", minWidth: 60 }}>
                              <div style={{ width: `${median === null ? 0 : Math.max((100 * median) / maxMedian, 2)}%`, height: "100%", borderRadius: 4, background: colorOf(code) }} />
                            </div>
                            <strong style={{ width: 64, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtSec(row.mediana_seg)}</strong>
                          </div>
                        </td>
                        <td style={tdNum}>{fmtSec(row.p75_seg)}</td>
                        {compareControl && (
                          <td style={{ ...tdNum, color: diff?.color ?? "#cbd5e1", fontWeight: 600 }}>
                            {!diff ? "—" : diff.text === "igual" ? "igual" : `${diff.text} s`}
                          </td>
                        )}
                        <td style={tdNum}>{fmtSec(row.aporte_seg)}</td>
                        <td style={tdNum}>{fmtCount(row.personas)} <span style={{ color: MUTED }}>({fmtPct(pctOf(num(row.personas) ?? 0, people))})</span></td>
                        <td style={{ ...tdNum, color: errorPct >= 10 ? BAD : INK }}>{fmtPct(row.con_error_pct)}</td>
                        <td style={tdNum}>{fmtCount(row.ediciones_promedio)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableWrap>
            </div>
          );
        })}
        <div style={{ fontSize: 12, color: "#94a3b8" }}>
          Mide el tiempo con el cursor dentro de cada campo: no cuenta leer ni pensar fuera de él. En las fotos de documentos puede incluir el tiempo con la cámara abierta.
          Con pocas personas por campo las medianas varían mucho: leerlas junto a "Lo usaron".
        </div>
      </>
    );
  };

  // Distribución del tiempo de un paso: cuántas personas tardan cuánto (una curva por versión).
  // Con fixedStep se muestra solo ese paso, sin selector.
  const renderStepDistribution = (fixedStep?: string) => {
    const rows = data?.stepTimes ?? [];
    const steps = TIME_STEPS.filter((step) => !step.stagesOnly || anyStages);
    const step = steps.find((item) => item.key === (fixedStep ?? timeStep)) ?? steps[0];
    const ofStep = rows.filter((row) => row.paso === step.key);
    const seriesCodes = Array.from(new Set(ofStep.map((row) => String(row.variante))))
      .sort((a, b) => variants.findIndex((v) => v.code === a) - variants.findIndex((v) => v.code === b));
    const valuesOf = (code: string): number[] =>
      ofStep.filter((row) => row.variante === code).map((row) => num(row.seg) ?? 0).sort((a, b) => a - b);
    const all = ofStep.map((row) => num(row.seg) ?? 0).sort((a, b) => a - b);
    const limit = Math.max(quantile(all, 0.95) ?? 0, 1);
    const width = binWidthFor(limit);
    const binCount = Math.max(Math.ceil(limit / width), 1);
    const labels = Array.from({ length: binCount }, (_, index) => fmtSec(index * width));
    const series = seriesCodes.map((code) => {
      const values = valuesOf(code);
      const counts = new Array<number>(binCount).fill(0);
      let outside = 0;
      values.forEach((value) => {
        const bin = Math.floor(value / width);
        if (bin < binCount) counts[bin] += 1;
        else outside += 1;
      });
      const color = code === ALL_SERIES ? "#475569" : colorOf(code);
      const name = code === ALL_SERIES ? "Todas las personas" : nameOf(code);
      return { code, color, name, values, counts, outside, median: quantile(values, 0.5) };
    });

    const chart = {
      labels,
      datasets: series.map((item) => ({
        label: item.name,
        data: item.counts.map((count) => (timeAsShare ? pctOf(count, item.values.length) ?? 0 : count)),
        borderColor: item.color,
        backgroundColor: withAlpha(item.color, 0.15),
        fill: true,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      })),
    };
    const unit = timeAsShare ? " %" : "";
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<"line">[]) => `Entre ${labels[items[0]?.dataIndex ?? 0]} y ${fmtSec(((items[0]?.dataIndex ?? 0) + 1) * width)}`,
            label: (ctx: TooltipItem<"line">) => `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toLocaleString("es-CL")}${timeAsShare ? " %" : " personas"}`,
          },
        },
      },
      scales: {
        y: { beginAtZero: true, grid: { color: "#f1f5f9" }, title: { display: true, text: timeAsShare ? "% de personas" : "Personas" }, ticks: { callback: (v: number | string) => `${v}${unit}` } },
        x: { grid: { display: false }, title: { display: true, text: "Tiempo" }, ticks: { maxRotation: 0, autoSkipPadding: 12 } },
      },
    };

    const chip = (active: boolean): React.CSSProperties => ({
      border: `1px solid ${active ? "#94a3b8" : "#e2e8f0"}`,
      background: active ? "#fff" : "transparent",
      color: active ? INK : MUTED,
      fontWeight: active ? 700 : 500,
      borderRadius: 999,
      padding: "5px 12px",
      fontSize: 13,
      cursor: "pointer",
    });

    return (
      <div style={card} data-step-distribution={step.key}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          {fixedStep ? <div /> : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {steps.map((item) => (
                <button key={item.key} onClick={() => setTimeStep(item.key)} style={chip(item.key === step.key)}>{item.label}</button>
              ))}
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#475569", cursor: "pointer" }}>
            <input type="checkbox" checked={timeAsShare} onChange={(event) => setTimeAsShare(event.target.checked)} />
            En % de cada versión
          </label>
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>{step.hint}</div>
        {series.length === 0 ? (
          <Empty message="Aún no hay personas con este paso medido." />
        ) : (
          <>
            <div style={{ height: 280 }}><Line data={chart} options={options} /></div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", marginTop: 12 }}>
              {series.map((item) => (
                <div key={item.code} style={{ display: "flex", alignItems: "center", fontSize: 13, color: INK }}>
                  <VariantDot color={item.color} />
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  <span style={{ color: MUTED, marginLeft: 6 }}>
                    · {fmtCount(item.values.length)} personas · mediana {fmtSec(item.median)}
                    {item.outside > 0 ? ` · ${fmtCount(item.outside)} tardaron más de ${fmtSec(binCount * width)} (fuera del gráfico)` : ""}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Identificación y código OTP: cuántos lo pidieron, cuánto tardan y quiénes no postularon.
  const renderOtp = () => {
    const otp = data?.otp?.[0];
    const pending = (data?.otpPending ?? []).filter((row) =>
      otpFilter === "todos" || (otpFilter === "verificaron") === (row.verifico === true));
    const requested = num(otp?.pidieron_codigo) ?? 0;
    const pendingOverLimit = excedeLimiteDescarga(pending.length);

    const kpi = (label: string, value: string, sub?: string, accent?: string) => (
      <div style={{ ...card, flex: "1 1 200px", minWidth: 0 }}>
        <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: accent ?? INK, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
      </div>
    );

    const exportPending = () => downloadCsv(
      `otp-sin-postular-${selectedCode}.csv`,
      ["RUT", "Teléfono", "Nombre", "Formulario", "Pidió el código", "Códigos pedidos", "Verificó el código", "Intentos fallidos", "Bloqueado"],
      pending.slice(0, MAX_REGISTROS_DESCARGA).map((row) => [
        row.rut, row.telefono, row.nombre, row.formulario, fmtDate(row.fecha_codigo, true), row.codigos,
        row.verifico ? "Sí" : "No", row.intentos_fallidos, row.bloqueado ? "Sí" : "No",
      ]),
    );

    if (!otp) return <Empty message="No se pudieron cargar los datos del código OTP." />;

    return (
      <>
        <Block
          title="Código OTP"
          subtitle="El código solo se pide a quien ya existe en la base con ese RUT y teléfono; los nuevos entran directo al formulario. Cuenta personas (RUT) que se identificaron en los formularios del experimento."
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {kpi("Ingresaron RUT y teléfono", fmtCount(otp.se_identificaron), `${fmtCount(otp.nuevos)} nuevos entraron sin código`)}
            {kpi("Pidieron código OTP", fmtCount(requested), `${fmtPct(otp.pidieron_codigo_pct)} de los que se identificaron · ${fmtCount(otp.codigos_enviados)} códigos enviados (${fmtCount(otp.pidieron_reenvio)} pidieron reenvío)`)}
            {kpi("Verificaron el código", fmtPct(otp.verificacion_pct), `${fmtCount(otp.verificaron)} de ${fmtCount(requested)} · ${fmtCount(otp.no_verificaron)} no lo verificaron${num(otp.bloqueados) ? ` (${fmtCount(otp.bloqueados)} bloqueados por 3 intentos)` : ""}`)}
            {kpi("Tiempo en el código (mediana)", fmtSec(otp.otp_mediana_seg), `p75 ${fmtSec(otp.otp_p75_seg)} · p90 ${fmtSec(otp.otp_p90_seg)} · desde el envío hasta verificarlo`)}
            {kpi(
              "Pidieron código y no postularon",
              fmtCount(otp.pidieron_codigo_sin_postular),
              `${fmtPct(otp.sin_postular_pct)} de los que pidieron · ${fmtCount(otp.no_verificaron_sin_postular)} no verificaron, ${fmtCount(otp.verificaron_sin_postular)} verificaron y no enviaron`,
              num(otp.pidieron_codigo_sin_postular) ? BAD : INK,
            )}
          </div>
        </Block>

        <Block title="Cuánto tardan en el código" subtitle="Personas según el tiempo entre que se envía el código y lo verifican.">
          {renderStepDistribution("OTP")}
        </Block>

        <Block title="Pidieron el código y nunca postularon" subtitle="Sin ninguna postulación (en cualquier formulario) desde que empezó el experimento. Sirve para contactarlas.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#475569" }}>
              Mostrar
              <select value={otpFilter} onChange={(event) => setOtpFilter(event.target.value as typeof otpFilter)} style={{ fontSize: 13, padding: "5px 8px", borderRadius: 8, border: "1px solid #cbd5e1" }}>
                <option value="todos">Todas ({fmtCount(data?.otpPending.length ?? 0)})</option>
                <option value="no_verificaron">No verificaron el código</option>
                <option value="verificaron">Verificaron pero no enviaron</option>
              </select>
            </label>
            <button
              onClick={exportPending}
              disabled={pending.length === 0}
              title={pendingOverLimit ? AVISO_DESCARGA_SIN_SELECCION : undefined}
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "6px 12px", cursor: pending.length ? "pointer" : "default", color: "#334155" }}
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Descargar CSV
            </button>
          </div>
          {pendingOverLimit && (
            <div style={{ fontSize: 12, color: "#b45309", marginBottom: 8 }}>
              {AVISO_DESCARGA_SIN_SELECCION} Hay {fmtCount(pending.length)} en la lista.
            </div>
          )}
          {pending.length === 0 ? (
            <Empty message="No hay personas en esta lista." />
          ) : (
            <div style={{ maxHeight: 520, overflowY: "auto", borderRadius: 14 }}>
              <TableWrap>
                <thead>
                  <tr>
                    {["RUT", "Teléfono", "Nombre", "Formulario", "Pidió el código", "Códigos", "¿Verificó?", "Intentos fallidos"].map((label) => (
                      <th key={label} style={{ ...th, position: "sticky", top: 0, background: "#fff", textAlign: ["Códigos", "Intentos fallidos"].includes(label) ? "right" : "left" }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pending.map((row) => (
                    <tr key={String(row.rut)} data-otp-pending={String(row.rut)}>
                      <td style={{ ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{String(row.rut ?? "—")}</td>
                      <td style={{ ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{String(row.telefono ?? "—")}</td>
                      <td style={td}>{String(row.nombre ?? "—")}</td>
                      <td style={td}>{String(row.formulario ?? "—")}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(row.fecha_codigo, true)}</td>
                      <td style={tdNum}>{fmtCount(row.codigos)}</td>
                      <td style={td}>
                        {row.verifico
                          ? <Chip label="Sí" color="#166534" bg="#dcfce7" />
                          : <Chip label={row.bloqueado ? "No · bloqueado" : "No"} color="#b91c1c" bg="#fee2e2" />}
                      </td>
                      <td style={tdNum}>{fmtCount(row.intentos_fallidos)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </Block>
      </>
    );
  };

  // ── charts ─────────────────────────────────────────────────────────────────
  const days = Array.from(new Set((data?.daily ?? []).map((row) => String(row.dia)))).sort();
  const dailyValue = (day: string, variant: string, key: string): number =>
    num((data?.daily ?? []).find((row) => String(row.dia) === day && row.variante === variant)?.[key]) ?? 0;
  const dailyChart = (key: string) => ({
    labels: days.map(fmtDay),
    datasets: variants.map((variant) => ({
      label: nameOf(variant.code),
      data: days.map((day) => dailyValue(day, variant.code, key)),
      backgroundColor: colorOf(variant.code),
      borderRadius: 4,
      maxBarThickness: 36,
    })),
  });

  const barOptions = (unit = "") => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" as const, labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: { callbacks: { label: (ctx: TooltipItem<"bar">) => `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toLocaleString("es-CL")}${unit}` } },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "#f1f5f9" }, ticks: { callback: (v: number | string) => `${v}${unit}` } },
      x: { grid: { display: false } },
    },
  });

  // ── render ─────────────────────────────────────────────────────────────────
  if (listLoading) {
    return <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Cargando experimentos…</div>;
  }
  if (experiments.length === 0) {
    return (
      <div style={{ padding: 32 }}>
        <Empty message={error || "No hay experimentos A/B configurados."} />
      </div>
    );
  }

  const status = STATUS_STYLE[experiment?.status ?? ""] ?? STATUS_STYLE.BORRADOR;
  const runningDays = daysSince(experiment?.startedAt ?? null);
  const minSample = experiment?.minimumSamplePerVariant ?? null;
  const hasPeople = Boolean(overviewBy.TOTAL) && countOf("TOTAL", "asignadas") > 0;

  return (
    <div style={{ padding: "8px 24px 48px", maxWidth: 1280, margin: "0 auto" }}>
      {/* ── encabezado ───────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, flex: "1 1 320px", minWidth: 0 }}>
            <ScienceOutlinedIcon style={{ color: VARIANT_COLORS[0] }} />
            <select
              value={selectedCode}
              onChange={(event) => setSelectedCode(event.target.value)}
              style={{ fontSize: 14, padding: "7px 10px", borderRadius: 8, border: "1px solid #cbd5e1", flex: "1 1 240px", minWidth: 0, maxWidth: 460 }}
            >
              {experiments.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name} · {STATUS_STYLE[item.status]?.label ?? item.status}
                </option>
              ))}
            </select>
            <Chip {...status} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#475569", cursor: "pointer" }}>
              <input type="checkbox" checked={includeForced} onChange={(event) => setIncludeForced(event.target.checked)} />
              Incluir pruebas (?ab=)
            </label>
            <button
              onClick={() => void loadMetrics()}
              disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "6px 12px", cursor: loading ? "default" : "pointer", color: "#334155" }}
            >
              <RefreshIcon style={{ fontSize: 16 }} />
              {loading ? "Actualizando…" : "Actualizar"}
            </button>
            {updatedAt && <span style={{ fontSize: 12, color: "#94a3b8" }}>{updatedAt.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}</span>}
          </div>
        </div>
        {experiment && (
          <div style={{ fontSize: 13, color: MUTED, marginTop: 10 }}>
            Desde {fmtDate(experiment.startedAt)} ({runningDays === null ? "—" : `${runningDays} días`}) · {experiment.enrolledForms} formulario(s) inscrito(s) · ventana {experiment.completionWindowHours} h
            {minSample ? ` · meta ${minSample.toLocaleString("es-CL")} personas por variante` : ""}
            {experiment.hypothesis && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: "pointer", color: "#475569" }}>Ver hipótesis</summary>
                <p style={{ margin: "6px 0 0" }}>{experiment.hypothesis}</p>
                {experiment.conclusion && <p style={{ margin: "6px 0 0" }}><strong>Conclusión:</strong> {experiment.conclusion}</p>}
              </details>
            )}
          </div>
        )}
        {includeForced && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#92400e", background: "#fffbeb", borderRadius: 8, padding: "6px 10px" }}>
            Incluye asignaciones de prueba (?ab=): sirven para revisar el flujo, no para decidir.
          </div>
        )}
      </div>

      {/* ── sub-pestañas ─────────────────────────────────────────────────── */}
      <div role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20, background: "#eef2f7", borderRadius: 12, padding: 4, width: "fit-content", maxWidth: "100%" }}>
        {SUB_TABS.map((tab) => {
          const active = subTab === tab.key;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={active}
              onClick={() => setSubTab(tab.key)}
              style={{
                border: "none",
                borderRadius: 9,
                padding: "8px 16px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                background: active ? "#fff" : "transparent",
                color: active ? INK : MUTED,
                boxShadow: active ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 20, color: BAD, display: "flex", alignItems: "center", gap: 8 }}>
          <ErrorOutlineIcon /> {error}
        </div>
      )}

      {loading && !data && <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Calculando métricas…</div>}

      {data && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity .2s" }}>
          {/* ── resumen ─────────────────────────────────────────────────── */}
          {subTab === "resumen" && (
            <>
              {!hasPeople ? (
                <Empty message="Aún no hay personas asignadas en este experimento." />
              ) : (
                <>
                  <Block title="Cómo le va a cada versión" subtitle="Lo que más pesa en la decisión. El número de color compara contra el control: verde es mejor, rojo es peor.">
                    {renderScorecards()}
                  </Block>
                  <Block title="Dónde está hoy cada persona" subtitle="Todas las personas asignadas, según hasta dónde llegaron.">
                    {renderCurrentState()}
                  </Block>
                </>
              )}

              <Block title="Formularios participantes" subtitle="Formularios inscritos en el experimento.">
                {forms.length === 0 ? (
                  <Empty message="Ningún formulario está inscrito en este experimento." />
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))", gap: 16 }}>
                    {forms.map(({ info, byVariant }) => (
                      <div key={String(info.ContextoFormularioId)} style={card}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>{String(info.formulario ?? "—")}</div>
                            <div style={{ fontSize: 13, color: MUTED }}>{String(info.cliente ?? "Sin cliente")}</div>
                          </div>
                          {info.inscrito_activo ? <Chip label="Participando" color="#166534" bg="#dcfce7" /> : <Chip label="Retirado" color="#475569" bg="#e2e8f0" />}
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8", margin: "6px 0 12px" }}>
                          {[info.tipo, info.fuente, info.reclutador].filter(Boolean).map(String).join(" · ") || "—"}
                          {` · desde ${fmtDate(info.fecha_incorporacion)}`}
                          {info.fecha_retiro ? ` hasta ${fmtDate(info.fecha_retiro)}` : ""}
                          {info.vigente === false ? " · formulario no vigente" : ""}
                        </div>
                        {variants.map((variant) => {
                          const row = byVariant[variant.code];
                          return (
                            <div key={variant.code} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #f1f5f9", fontSize: 13 }}>
                              <span style={{ display: "flex", alignItems: "center", color: "#334155" }}>
                                <VariantDot color={colorOf(variant.code)} />
                                {nameOf(variant.code)}
                              </span>
                              <span style={{ color: INK }}>
                                <strong>{fmtCount(row?.postularon ?? 0)}</strong> de {fmtCount(row?.asignadas ?? 0)} postularon
                                <span style={{ color: MUTED }}> · {fmtCount(row?.completas ?? 0)} completas</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </Block>
            </>
          )}

          {/* ── camino ──────────────────────────────────────────────────── */}
          {subTab === "camino" && (
            !hasPeople ? (
              <Empty message="Aún no hay personas asignadas en este experimento." />
            ) : (
              <>
                <Block title="Camino del postulante" subtitle="Cuántas personas llegan a cada paso. El % de la derecha es sobre las que entraron; entre pasos se ve cuántos siguen y cuántos se van.">
                  {renderPath()}
                </Block>
                <Block title="Cuánto se demora el camino" subtitle="Medianas de tiempo de cada tramo.">
                  {renderTimeline()}
                </Block>
              </>
            )
          )}

          {/* ── código OTP ──────────────────────────────────────────────── */}
          {subTab === "otp" && renderOtp()}

          {/* ── tiempo por campo ─────────────────────────────────────────── */}
          {subTab === "campos" && (
            <>
              <Block title="Cuánto tardan en cada paso" subtitle="Cuántas personas tardan cuánto en el paso elegido: los picos muestran los tiempos más comunes.">
                {renderStepDistribution()}
              </Block>
              <Block title="Tiempo por campo" subtitle="Qué campos toman más tiempo en cada versión y paso: sirve para decidir qué simplificar o cambiar de paso.">
                {renderFields()}
              </Block>
            </>
          )}

          {/* ── detalle ─────────────────────────────────────────────────── */}
          {subTab === "detalle" && (
            <>
              <Block title="Todas las métricas" subtitle="Comparación por variante. Pasa el cursor sobre ⓘ para ver cómo se calcula cada una.">
                {hasPeople ? renderDetail() : <Empty message="Aún no hay personas asignadas en este experimento." />}
              </Block>
              <Block title="Evolución diaria" subtitle="Por fecha de asignación: ¿el efecto se mantiene en el tiempo?">
                {days.length === 0 ? (
                  <Empty message="Aún no hay asignaciones." />
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))", gap: 16 }}>
                    <div style={card}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 10 }}>Personas asignadas por día</div>
                      <div style={{ height: 220 }}><Bar data={dailyChart("asignadas")} options={barOptions()} /></div>
                    </div>
                    <div style={card}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 10 }}>Tasa de conversión por día</div>
                      <div style={{ height: 220 }}><Bar data={dailyChart("conversion_pct")} options={barOptions(" %")} /></div>
                    </div>
                  </div>
                )}
              </Block>
              <Block title="Por dispositivo" subtitle="¿La variante funciona igual en móvil y en escritorio?">
                {data.segments.length === 0 ? (
                  <Empty message="Aún no hay asignaciones." />
                ) : (
                  <TableWrap>
                    <thead>
                      <tr>
                        {["Dispositivo", "Variante", "Asignadas", "Postularon", "Conversión", "Rebote", "Completas"].map((label) => (
                          <th key={label} style={{ ...th, textAlign: ["Dispositivo", "Variante"].includes(label) ? "left" : "right" }}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.segments.map((row, index) => (
                        <tr key={index}>
                          <td style={td}>{DEVICE_LABEL[String(row.dispositivo)] ?? String(row.dispositivo)}</td>
                          <td style={td}><span style={{ display: "flex", alignItems: "center" }}><VariantDot color={colorOf(String(row.variante))} />{nameOf(String(row.variante))}</span></td>
                          <td style={tdNum}>{fmtCount(row.asignadas)}</td>
                          <td style={tdNum}>{fmtCount(row.postularon)}</td>
                          <td style={tdNum}><strong>{fmtPct(row.conversion_pct)}</strong></td>
                          <td style={tdNum}>{fmtPct(row.rebote_pct)}</td>
                          <td style={tdNum}>{fmtCount(row.completas)} ({fmtPct(row.completas_pct)})</td>
                        </tr>
                      ))}
                    </tbody>
                  </TableWrap>
                )}
              </Block>
            </>
          )}

          {/* ── diagnóstico ─────────────────────────────────────────────── */}
          {subTab === "diagnostico" && (
            <>
              <Block title="Significancia estadística" subtitle="Métricas principales con intervalo de confianza del 95 %. Base: personas maduras que vieron el formulario.">
                {data.primary.length === 0 ? (
                  <Empty message={`Aún no hay personas maduras (asignadas hace más de ${experiment?.completionWindowHours ?? 48} h) que hayan visto el formulario.`} />
                ) : (
                  <TableWrap>
                    <thead>
                      <tr>
                        {["Métrica", "Variante", "Personas", "Tasa", "Δ vs control", "IC 95 %", "Lectura"].map((label) => (
                          <th key={label} style={{ ...th, textAlign: ["Métrica", "Variante", "Lectura"].includes(label) ? "left" : "right" }}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.primary.map((row, index) => {
                        const readingStyle = READING_STYLE[String(row.lectura)] ?? READING_STYLE.SIN_DIFERENCIA;
                        const hasCi = num(row.ic95_inf_pp) !== null;
                        const deltaPp = num(row.delta_pp);
                        return (
                          <tr key={index}>
                            <td style={td}>{PRIMARY_LABEL[String(row.metrica)] ?? String(row.metrica)}</td>
                            <td style={td}><span style={{ display: "flex", alignItems: "center" }}><VariantDot color={colorOf(String(row.variante))} />{nameOf(String(row.variante))}</span></td>
                            <td style={tdNum}>{fmtCount(row.personas)}</td>
                            <td style={tdNum}><strong>{fmtPct(row.tasa_pct)}</strong></td>
                            <td style={tdNum}>{deltaPp === null ? "—" : `${deltaPp > 0 ? "+" : ""}${fmtCount(deltaPp)} pp`}</td>
                            <td style={tdNum}>{hasCi ? `${fmtCount(row.ic95_inf_pp)} a ${fmtCount(row.ic95_sup_pp)} pp` : "—"}</td>
                            <td style={td}><Chip {...readingStyle} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </TableWrap>
                )}
              </Block>

              <Block title="Embudo de personas maduras" subtitle="Solo quienes ya tuvieron tiempo de terminar: es la base justa para comparar.">
                {data.funnel.length === 0 ? (
                  <Empty message="Aún no hay personas maduras." />
                ) : (
                  <TableWrap>
                    <thead>
                      <tr>
                        <th style={th}>Paso</th>
                        {variants.map((variant) => (
                          <th key={variant.code} style={{ ...th, textAlign: "right" }}>{nameOf(variant.code)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(new Set(data.funnel.map((row) => String(row.etapa)))).map((step) => (
                        <tr key={step}>
                          <td style={td}>{FUNNEL_LABEL[step] ?? step}</td>
                          {variants.map((variant) => {
                            const row = data.funnel.find((item) => item.variante === variant.code && item.etapa === step);
                            return (
                              <td key={variant.code} style={tdNum}>
                                <strong>{fmtCount(row?.n)}</strong> <span style={{ color: MUTED }}>({fmtPct(row?.pct_vs_asignadas)})</span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </TableWrap>
                )}
              </Block>

              {anyStages && data.delayEffect.length > 0 && (
                <Block title="Efecto del retraso del WhatsApp" subtitle="El retraso se sortea: compara si conviene escribir antes o después.">
                  <TableWrap>
                    <thead>
                      <tr>
                        {["Variante", "Retraso", "Invitaciones", "Abrió", "Completa en ventana"].map((label) => (
                          <th key={label} style={{ ...th, textAlign: ["Variante", "Retraso"].includes(label) ? "left" : "right" }}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.delayEffect.map((row, index) => (
                        <tr key={index}>
                          <td style={td}>{nameOf(String(row.variante))}</td>
                          <td style={td}>{fmtCount(row.desde_min)}–{fmtCount(row.hasta_min)} min</td>
                          <td style={tdNum}>{fmtCount(row.invitaciones)}</td>
                          <td style={tdNum}>{fmtPct(row.abrio_pct)}</td>
                          <td style={tdNum}>{fmtPct(row.completa_en_ventana_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </TableWrap>
                </Block>
              )}

              {data.timeBreakdown.length > 0 && (
                <Block title="Desglose del tiempo total" subtitle="Quienes completaron por etapas: qué parte es formulario y qué parte es espera.">
                  <TableWrap>
                    <thead>
                      <tr>
                        {["Variante", "Tramo", "Personas", "Mediana", "p75", "p90"].map((label) => (
                          <th key={label} style={{ ...th, textAlign: ["Variante", "Tramo"].includes(label) ? "left" : "right" }}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.timeBreakdown.map((row, index) => (
                        <tr key={index}>
                          <td style={td}>{nameOf(String(row.variante))}</td>
                          <td style={td}>{SEGMENT_LABEL[String(row.tramo)] ?? String(row.tramo)}</td>
                          <td style={tdNum}>{fmtCount(row.personas)}</td>
                          <td style={tdNum}><strong>{fmtMin(row.mediana_min)}</strong></td>
                          <td style={tdNum}>{fmtMin(row.p75_min)}</td>
                          <td style={tdNum}>{fmtMin(row.p90_min)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </TableWrap>
                </Block>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))", gap: 24 }}>
                <Block title="Sorteo y reparto" subtitle="¿El sorteo reparte según los pesos?">
                  {data.srm.length > 0 && (
                    <div style={{ ...card, marginBottom: 14 }}>
                      {data.srm.map((row) => (
                        <div key={String(row.variante)} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                          <span style={{ display: "flex", alignItems: "center" }}><VariantDot color={colorOf(String(row.variante))} />{nameOf(String(row.variante))}</span>
                          <span>
                            <strong>{fmtPct(row.observado_pct)}</strong> <span style={{ color: MUTED }}>observado · {fmtPct(row.esperado_pct)} esperado</span>
                          </span>
                        </div>
                      ))}
                      <div style={{ fontSize: 12, marginTop: 8, color: data.srm.some((row) => row.alerta_srm === true) ? BAD : GOOD, fontWeight: 600 }}>
                        {data.srm.some((row) => row.alerta_srm === true) ? "✗ El reparto NO calza con los pesos: revisar el sorteo." : "✓ Reparto consistente con los pesos."}
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}> (χ² {fmtCount(data.srm[0]?.chi2)}, crítico {fmtCount(data.srm[0]?.chi2_critico)})</span>
                      </div>
                    </div>
                  )}
                  {data.exposure.length > 0 && (
                    <TableWrap>
                      <thead>
                        <tr>
                          {["Resultado del sorteo", "Sesiones", "Personas"].map((label) => (
                            <th key={label} style={{ ...th, textAlign: label.startsWith("Resultado") ? "left" : "right" }}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.exposure.map((row, index) => (
                          <tr key={index}>
                            <td style={td}>
                              {EXPOSURE_LABEL[String(row.resultado)] ?? String(row.resultado)}
                              {row.motivo ? <span style={{ color: MUTED }}> · {String(row.motivo).toLowerCase()}</span> : null}
                            </td>
                            <td style={tdNum}>{fmtCount(row.sesiones)} <span style={{ color: MUTED }}>({fmtPct(row.sesiones_pct)})</span></td>
                            <td style={tdNum}>{fmtCount(row.personas)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </TableWrap>
                  )}
                </Block>
                <Block title="Salud del sistema" subtitle="Todo el sistema de postulación por etapas. Lo esperado es 0.">
                  {data.health.length === 0 ? (
                    <Empty message="No se pudo consultar la salud del sistema." />
                  ) : (
                    <div style={card}>
                      {data.health.map((row) => {
                        const value = num(row.valor) ?? 0;
                        return (
                          <div key={String(row.chequeo)} data-health={String(row.chequeo)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                            <span style={{ color: "#334155" }}>{HEALTH_LABEL[String(row.chequeo)] ?? String(row.chequeo)}</span>
                            <span style={{ fontWeight: 700, color: value > 0 ? BAD : GOOD }}>{value > 0 ? `✗ ${fmtCount(value)}` : "✓ 0"}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Block>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PanelExperimentoAB;
